import './_liveSummary.scss'

import { useMemo } from 'react'

import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import type SwiperCore from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'

import placeholderAvatar from 'assets/images/user_placeholder.png'
import * as spriteIcons from 'assets/svg/sprite'
import { Button } from 'components/Button/Button'
import { SwitchButton } from 'components-ref/atoms'
import { type EndStreamStats } from 'features/LiveStream/LiveStreamCreator/LiveStreamCreator'
import 'swiper/css'

type LiveSummaryProps = {
  stats: EndStreamStats | null
  saveToVault: boolean
  onToggleSaveToVault: () => void
  onClose: () => void | Promise<void>
}

type TopTipper = NonNullable<EndStreamStats>['top_tippers'][number]

type RenderTipper = TopTipper & {
  __renderKey: string
}

const orderCenterOut = <T,>(items: T[]): T[] => {
  const leftSide = items.filter((_, index) => index % 2 === 0).reverse()
  const rightSide = items.filter((_, index) => index % 2 === 1)

  return [...leftSide, ...rightSide]
}

const LiveSummary = ({
  stats,
  saveToVault,
  onToggleSaveToVault,
  onClose,
}: LiveSummaryProps) => {
  const { t } = useTranslation()

  const topTippers = stats?.top_tippers ?? []

    const StatChange = ({
    percentage,
    }: {
    percentage?: number | null
    }) => {
    if (percentage == null) return null

    const isPositive = percentage >= 0

    return (
        <div
        className={`summary__stat-change ${
            isPositive ? 'increment' : 'decrement'
        }`}
        >
        {isPositive ? (
            <spriteIcons.IconUpArrow />
        ) : (
            <spriteIcons.IconDownArrow />
        )}
        <span>{Math.abs(percentage)}%</span>
        </div>
    )
    }
  const formatDuration = (seconds?: number | null) => {
    const total = Math.floor(Number(seconds ?? 0))

    const mins = Math.floor(total / 60)
    const secs = total % 60

    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
  }

  const displayTippers = useMemo<RenderTipper[]>(() => {
    // TEST MODE: each user appears 3 times
    const source: RenderTipper[] = topTippers.flatMap((tipper, index) => [
      { ...tipper, __renderKey: `${tipper.id}-${index}-1` },
      { ...tipper, __renderKey: `${tipper.id}-${index}-2` },
      { ...tipper, __renderKey: `${tipper.id}-${index}-3` },
    ])

    // NORMAL MODE: one slide per user
    // const source: RenderTipper[] = topTippers.map((tipper, index) => ({
    //   ...tipper,
    //   __renderKey: `${tipper.id}-${index}`,
    // }))

    return orderCenterOut(source)
  }, [topTippers])

  const initialSlide = useMemo(() => {
    if (!displayTippers.length) return 0

    const centeredIndex = displayTippers.findIndex(tipper =>
      tipper.__renderKey.endsWith('-1')
    )

    return centeredIndex >= 0
      ? centeredIndex
      : Math.floor(displayTippers.length / 2)
  }, [displayTippers])

  return (
    <div className='summary'>
      <div className='summary__top'>
        <div className='summary__viewers'>
          <div className='summary__viewers--value'>{stats?.views ?? 0}</div>
          <div className='summary__viewers--title'>{t('viewers')}</div>
        </div>

        <div className='summary__tips'>
          <div className='summary__tips--value'>
            <span>$</span>
            {stats?.total_tips ?? 0}
          </div>
          <div className='summary__tips--title'>{t('totalTips')}</div>
        </div>

        {displayTippers.length > 0 && (
          <div className='summary__slider'>
            <Swiper
              onInit={(core: SwiperCore) => {
                core.slideTo(initialSlide, 0)
              }}
              className='streamSummarySwiper'
              freeMode
              slidesPerView='auto'
              centeredSlides
            >
              {displayTippers.map(tipper => (
                <SwiperSlide key={tipper.__renderKey}>
                  <AvatarWrapper>
                    <img
                      src={tipper.avatar ?? placeholderAvatar}
                      alt={tipper.username ?? tipper.name ?? ''}
                      onError={e => {
                        e.currentTarget.src = placeholderAvatar
                      }}
                    />
                  </AvatarWrapper>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        )}
      </div>

      <div className='summary__bottom'>
        <div className='summary__statsCard'>
          <div className='summary__statsCard--inner'>
            <div className='summary__statsCard--top'>
              <div className='stat'>
                <div className='stat__text stat__whitetext'>
                  {t('overallRevenue')}
                </div>
                <div className='stat__value stat__value--with-change'>
                <div className='summary__stat-change increment'>
                    <spriteIcons.IconUpArrow />
                    <span>477%</span>
                </div>

                <div className='blue'>
                    ${Number(stats?.total_tips ?? 0).toFixed(2)}
                </div>
                </div>
              </div>

              <div className='stat'>
                <div className='stat__text'>{t('tips')}</div>
                <div className='stat__value'>
                  <span>{stats?.tips_count ?? 0}</span>
                  <span>${Number(stats?.total_tips ?? 0).toFixed(2)}</span>
                </div>
              </div>

              <div className='summary__statsCard--divider' />
            </div>

            <div className='summary__statsCard--list'>
              <div className='stat'>
                <div className='stat__text'>{t('averageSession')}</div>
                <div className='stat__value'>
                    {formatDuration(stats?.average_viewer_duration)}
                </div>
              </div>

              <div className='stat'>
                <div className='stat__text'>{t('totalWatched')}</div>
                <div className='stat__value'>{stats?.views ?? '212:20'}</div>
              </div>

              <div className='stat'>
                <div className='stat__text'>{t('watchRate')}</div>
                <div className='stat__value'>{stats?.views}</div>
              </div>
              <div className='stat'>
                <div className='stat__text'>{t('messagesCount')}</div>
                <div className='stat__value'>{stats?.real_messages_count ?? 0}</div>
              </div>
            </div>
          </div>
        </div>

        <div className='summary__share'>
          <div className='summary__share--wrapper'>
            <div className='summary__share--text'>{t('saveLivestream')}</div>
            <SwitchButton active={saveToVault} toggle={onToggleSaveToVault} />
          </div>
        </div>

        <div className='summary__footer'>
          <Button
            text={t('ok')}
            color='transparent'
            font='mont-14-normal'
            type='transparent--white'
            width='16'
            height='5'
            padding='15'
            clickFn={onClose}
          />
        </div>
      </div>
    </div>
  )
}

export default LiveSummary

const AvatarWrapper = styled.div`
  width: 5rem;
  height: 5rem;
  margin-left: 1rem;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
    display: block;
  }
`